import { SpinnerIcon } from './icons';

/** Props for {@link Spinner}. */
export interface SpinnerProps {
  /** Diameter in pixels. */
  size?: number;
  /**
   * Accessible label announced to assistive tech. Pass an empty string only when
   * a nearby visible element already conveys the loading state.
   */
  label?: string;
  /** Optional extra classes. */
  className?: string;
}

/**
 * Indeterminate loading indicator.
 *
 * Accessibility: exposes `role="status"` with a visually-hidden label so screen
 * readers announce that content is loading, while sighted users see the spinning
 * icon. Honours `prefers-reduced-motion` via the global stylesheet, which slows
 * the `animate-spin` animation to a crawl.
 */
export function Spinner({ size = 20, label = 'Loading…', className = '' }: SpinnerProps): JSX.Element {
  return (
    <span role="status" className={`inline-flex items-center gap-2 text-slate-500 dark:text-slate-400 ${className}`.trim()}>
      <SpinnerIcon size={size} className="animate-spin" />
      {label ? <span className="text-sm">{label}</span> : <span className="sr-only">Loading…</span>}
    </span>
  );
}
